// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useMemo } from 'react'
import { useStickyState } from './useStickyState'
import { useDebouncedValue } from '../hooks/useDebouncedValue'

/**
 * SmartFilters — cascading filter dropdowns that are aware of current data.
 *
 * Each filter dropdown only shows values that exist in the ALREADY-FILTERED
 * dataset. So if you filter by Patient "Smith", the Drug dropdown shows only
 * drugs prescribed to Smith, not all drugs in the pharmacy.
 *
 * Usage:
 *   const { filtered, filterBar } = useSmartFilters(items, filterDefs)
 *   // filtered = items after all filters + search applied
 *   // filterBar = JSX for the filter row
 */

export interface FilterDef {
  key: string
  label: string
  getValue: (row: any) => string | null | undefined
}

interface SmartFilterState {
  [key: string]: string  // key → selected value ('' = no filter)
}

export function useSmartFilters<T>(
  items: T[],
  filterDefs: FilterDef[],
  // ASP-56: searchPredicates run the RAW query against a row (e.g. DOB in any of the six
  // supported formats, phone by digits) — a row matches when ANY field OR predicate matches.
  // D-VIEWS-STICKY: pass opts.persistKey to persist search + dropdown filters per view (survives
  // F5 and the open-Rx-and-back unmount). Omit it and the hook behaves exactly as before.
  opts?: { searchFields?: ((row: T) => string)[]; searchPredicates?: ((q: string, row: T) => boolean)[]; persistKey?: string
    /** RX-PERF-11: persist dropdown FILTERS but not the free-text search. A silently restored
     * search re-scopes the whole view on reload (pager totals flip from "all rows" to "rows
     * matching a search you typed days ago") — dropdown filters are visibly selected, a filled
     * search box is easy to miss. Default true (legacy); queue tabs pass false. */
    persistSearch?: boolean
    /** RX-PERF-12: controlled search — the caller owns the state (so it can drive query
     * enablement/keys declared BEFORE this hook runs). Overrides persistence entirely. */
    searchState?: [string, (v: string) => void]
    /** RX-PERF-13 (audit F5): minimum characters before the text search filters at all.
     * Default 1 (legacy). Queue tabs pass 2 to match the server ?q= threshold, so the row
     * set can't jump between "1-char client match" and "2-char client+server match". */
    minSearchLen?: number
    /** RX-PERF-13 (audit F4): hide the trailing "X of Y" counter. On surfaces that union
     * in server-side hits (StationQueue), this counter's denominator is only the loaded
     * subset — a second, contradictory total next to the pager's authoritative one. */
    showCount?: boolean }
) {
  const pk = opts?.persistKey ?? null
  const [filters, setFilters] = useStickyState<SmartFilterState>(pk ? `${pk}:filters` : null, {})
  const innerSearch = useStickyState<string>(pk && (opts?.persistSearch ?? true) ? `${pk}:search` : null, '')
  const [search, setSearch] = opts?.searchState ?? innerSearch
  // Input stays bound to `search` (responsive typing); the expensive filtering +
  // smart-option recomputes below run off the DEBOUNCED value so they fire once the
  // user pauses, not on every keystroke.
  const debouncedSearch = useDebouncedValue(search, 250)

  const setFilter = (key: string, val: string) =>
    setFilters(prev => ({ ...prev, [key]: val }))

  const clearAll = () => { setFilters({}); setSearch('') }

  // Apply filters in cascade order. For each filter dropdown, show only
  // values that survive ALL OTHER active filters (not the current one).
  // This makes the dropdowns "smart" — they narrow as you filter.
  const filtered = useMemo(() => {
    let rows = items

    // Apply each active filter
    for (const fd of filterDefs) {
      const val = filters[fd.key]
      if (val) rows = rows.filter(r => fd.getValue(r) === val)
    }

    // Apply search
    const q = debouncedSearch.trim().toLowerCase()
    const rawQ = debouncedSearch.trim()
    if (q && q.length >= (opts?.minSearchLen ?? 1) && (opts?.searchFields || opts?.searchPredicates)) {
      rows = rows.filter(r =>
        (opts.searchFields ?? []).some(fn => (fn(r) || '').toLowerCase().includes(q))
        || (opts.searchPredicates ?? []).some(fn => fn(rawQ, r))
      )
    }

    return rows
  }, [items, filters, debouncedSearch, filterDefs, opts?.searchFields, opts?.searchPredicates, opts?.minSearchLen])

  // For each filter, compute available options from rows filtered by
  // ALL OTHER filters (excluding this one). This is the "smart" part.
  const optionsPerFilter = useMemo(() => {
    const result: Record<string, string[]> = {}
    for (const fd of filterDefs) {
      // Apply all filters EXCEPT this one
      let rows = items
      for (const other of filterDefs) {
        if (other.key === fd.key) continue
        const val = filters[other.key]
        if (val) rows = rows.filter(r => other.getValue(r) === val)
      }
      // Also apply search
      const q = debouncedSearch.trim().toLowerCase()
      const rawQ = debouncedSearch.trim()
      if (q && q.length >= (opts?.minSearchLen ?? 1) && (opts?.searchFields || opts?.searchPredicates)) {
        rows = rows.filter(r =>
          (opts.searchFields ?? []).some(fn => (fn(r) || '').toLowerCase().includes(q))
          || (opts.searchPredicates ?? []).some(fn => fn(rawQ, r))
        )
      }
      const vals = new Set<string>()
      for (const r of rows) {
        const v = fd.getValue(r)
        if (v) vals.add(v)
      }
      result[fd.key] = [...vals].sort()
    }
    return result
  }, [items, filters, debouncedSearch, filterDefs, opts?.searchFields])

  const hasActiveFilters = Object.values(filters).some(v => v) || search.trim() !== ''

  const filterBar = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
      {opts?.searchFields && (
        <input
          type="search"
          placeholder="Search…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={searchStyle}
        />
      )}
      {filterDefs.map(fd => (
        <select
          key={fd.key}
          value={filters[fd.key] || ''}
          onChange={e => setFilter(fd.key, e.target.value)}
          style={selectStyle}
        >
          <option value="">All {fd.label.toLowerCase()}</option>
          {(optionsPerFilter[fd.key] || []).map(v => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      ))}
      {hasActiveFilters && (
        <button onClick={clearAll} style={clearBtnStyle}>Clear</button>
      )}
      {(opts?.showCount ?? true) && (
        <span style={{ fontSize: 11, color: 'var(--color-text-mute)', marginLeft: 'auto' }}>
          {filtered.length} of {items.length}
        </span>
      )}
    </div>
  )

  // D-VIEWS: a stable signature of the filter INPUTS (debounced search + dropdown selections).
  // Pagination keys its reset on this — not on the result COUNT — so an auto-refresh that changes
  // the row count never snaps the pager back to page 1, while changing a filter still does.
  const filterSig = useMemo(
    () => `${debouncedSearch}\u0001` + Object.keys(filters).sort().map(k => `${k}=${filters[k]}`).join('\u0002'),
    [debouncedSearch, filters],
  )
  return { filtered, filterBar, search, setSearch, filters, setFilter, clearAll, filterSig }
}

const searchStyle: React.CSSProperties = {
  padding: '6px 10px', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 12, width: 220, maxWidth: '100%',
}
const selectStyle: React.CSSProperties = {
  padding: '5px 8px', border: '1px solid var(--color-border)',
  borderRadius: 4, fontSize: 12, background: 'var(--color-surface)', maxWidth: 180,
}
const clearBtnStyle: React.CSSProperties = {
  fontSize: 11, padding: '4px 8px', background: 'transparent',
  border: '1px solid var(--color-border)', borderRadius: 4,
  cursor: 'pointer', color: 'var(--color-text-soft)',
}
